import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ChatRuntimeProvider,
  serializeAttachmentFiles,
  useAudio,
  useChatErrors,
  useChatRuntimeSurface,
  useConversationFlow,
  useFeedback,
  useStreetBeatChat,
} from '@dev-streetbeat/chat/react'
import type {
  AttachmentContent,
  AudioFormat,
  FeedbackRequest,
  MessageInput,
  Thread,
  Turn,
} from '@dev-streetbeat/chat/types'
import { Plus } from 'lucide-react'

import {
  blobToBase64,
  ChatComposer,
  FeedbackVariant,
  PendingActionBar,
  TranslationsProvider,
} from '@dev-streetbeat/chat-ui'
import { IconButton } from '../app/components/ui/IconButton'
import type {
  ArtifactRenderingConfig,
  ComposerAttachment,
  WelcomePrompt,
} from '@dev-streetbeat/chat-ui'
import type { AgentConfig } from './types'
import { useAceSession } from './useAceSession'
import { ClientBriefArtifact } from './ClientBriefArtifact'
import {
  CLIENT_BRIEF_REQUESTED_EVENT,
  CLIENT_CODE_SELECTED_EVENT,
  ClientListArtifact,
  type ClientBriefRequestedDetail,
  type ClientCodeSelectedDetail,
} from './ClientListArtifact'
import { ManagerTodosArtifact } from './ManagerTodosArtifact'
import { CredemConversationView } from './CredemConversationView'
import {
  ClientPickerMenu,
  ComposerStack,
  ContextChipBar,
  useChatContextSelection,
} from './composer-context'
import type { ClientRef } from './composer-context/types'
import { buildAgentChatConfig } from './chatConfig'
import { Loader } from '@/components/ui/loader'
import { useChatFavicon } from '@/hooks/useChatFavicon'

// Renderer registry for the agent's custom_data artifacts. Keyed by the
// `custom_data.type` discriminator emitted by the MCP tool. Add entries here
// as we wire more artifact types (client_list today; sector/country/etc.
// in the future).
const ARTIFACT_RENDERING: ArtifactRenderingConfig = {
  renderers: {
    customData: {
      'credem.client_list': ClientListArtifact,
      'credem.client_brief': ClientBriefArtifact,
      'credem.manager_todos': ManagerTodosArtifact,
    },
  },
}

const MAX_ATTACHMENT_BYTES = 30 * 1024 * 1024
const ATTACHMENT_ACCEPT = [
  'image/*',
  '.pdf',
  '.txt',
  '.md',
  '.csv',
  '.tsv',
  '.json',
  '.xml',
  '.yaml',
  '.yml',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
].join(',')

const SUGGESTED_PROMPTS = [
  'Which clients have the highest liquidity for a near-term portfolio review?',
  'Rank proactive opportunities for my book.',
  'Show bond exposure concentration across my top clients.',
]

const COMPOSER_PLACEHOLDERS = [
  'Chiedi una lista di clienti che rispecchi alcuni parametri...',
  'Chiedi un brief su un tuo cliente...',
  'Scrivi una mail di contatto per organizzare un incontro...',
  'Prepara la riunione con un tuo cliente...',
  'Pianifica una mail ricorrente...',
  'Chiedi informazioni sul tuo portafoglio clienti...',
]
const COMPOSER_PLACEHOLDER_ROTATION_MS = 4000
const AUDIO_UI_RELEASE_DELAY_MS = 240
const AUTO_SCROLL_BOTTOM_THRESHOLD_PX = 48
const EXISTING_THREAD_LOAD_SCROLL_SETTLE_MS = 800
const EXISTING_THREAD_LOAD_SCROLL_FALLBACK_DELAYS_MS = [50, 150, 350, 700] as const

const AUDIO_RENDERING_SELECTOR = [
  '[class*="WaveformVisualizer-module_container"]',
  '[class*="RecordingTimer-module_timer"]',
].join(',')

export function composerHasAudioRendering(root: HTMLElement | null): boolean {
  return Boolean(root?.querySelector(AUDIO_RENDERING_SELECTOR))
}

export function shouldShowComposerPlaceholder({
  composerIsEmpty,
  isAudioRenderingActive,
  isTranscribingAudio,
}: {
  composerIsEmpty: boolean
  isAudioRenderingActive: boolean
  isTranscribingAudio: boolean
}): boolean {
  return composerIsEmpty && !isAudioRenderingActive && !isTranscribingAudio
}

export function formatChatTimestamp(createdAt: Date | string): string {
  const date = new Date(createdAt)
  const now = new Date()
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()

  if (isToday) {
    return date.toLocaleTimeString('it-IT', { timeStyle: 'short' })
  }

  return date.toLocaleString('it-IT', {
    dateStyle: 'short',
    timeStyle: 'short',
  })
}

function isNearConversationBottom(element: HTMLElement): boolean {
  return (
    element.scrollHeight - element.scrollTop - element.clientHeight <=
    AUTO_SCROLL_BOTTOM_THRESHOLD_PX
  )
}

function conversationScrollKey(turns: readonly Turn[]): string {
  const lastTurn = turns.at(-1)
  if (!lastTurn) return 'empty'
  return `${turns.length}:${lastTurn.id}:${lastTurn.status}:${JSON.stringify(lastTurn.content)}`
}

function copyMessageTextToClipboard(text: string): void {
  void navigator.clipboard.writeText(text)
}

function audioFormatFromMimeType(format: string): AudioFormat {
  const normalized = format.toLowerCase()
  if (normalized.includes('m4a')) return 'm4a'
  if (normalized.includes('mp4')) return 'mp4'
  if (normalized.includes('mp3')) return 'mp3'
  if (normalized.includes('mpeg')) return 'mpeg'
  if (normalized.includes('mpga')) return 'mpga'
  if (normalized.includes('wav')) return 'wav'
  return 'webm'
}

function makeAttachment(file: File): ComposerAttachment {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  return { id, file }
}

function AceConversation({
  initialPrompt,
  autoSendInitialPrompt = false,
  initialClientContext,
  suggestedPrompts,
  showSuggestedPrompts = true,
  showWelcomeHeading = true,
  showNewChatButton = true,
  onConversationStartedChange,
  onRegisterSend,
  onRegisterFlow,
  initialSessionId,
  onThreadCreated,
  onThreadChange,
  onSelectedThreadUnavailable,
  threadMetadata,
  buildMessageMetadata,
  seedContext,
  selectedThreadId,
  enableFaviconStatus = false,
  existingThreadLoadScrollBehavior = 'auto',
}: {
  initialPrompt?: string
  autoSendInitialPrompt?: boolean
  /**
   * Cross-route hand-off: a `ClientRef` to pin into the composer chip on
   * mount. Used by surfaces outside the chat (e.g. Da fare row actions) so
   * landing in the chat already carries the client scope. When paired with
   * `autoSendInitialPrompt`, the queued prompt waits for the chip to commit
   * before sending so its outgoing metadata is scoped to this client.
   */
  initialClientContext?: ClientRef
  suggestedPrompts?: string[]
  showSuggestedPrompts?: boolean
  showWelcomeHeading?: boolean
  showNewChatButton?: boolean
  onConversationStartedChange?: (started: boolean) => void
  onRegisterSend?: (fn: (text: string) => void) => void
  onRegisterFlow?: (state: { isLoading: boolean }) => void
  initialSessionId?: string
  onThreadCreated?: (threadId: string) => void
  onThreadChange?: (threadId: string | null, thread?: Thread | null) => void
  onSelectedThreadUnavailable?: (threadId: string) => void
  /** Static, per-session metadata. Forwarded to sendMessage on the FIRST send
   *  in a brand-new thread; the SDK persists it as ACE Thread.metadata. */
  threadMetadata?: Record<string, unknown>
  /** Recomputed at send time. Forwarded to sendMessage on every subsequent
   *  send; persisted as ACE Message.metadata on that user message. */
  buildMessageMetadata?: () => Record<string, unknown> | undefined
  /** Hidden context line prepended to the user's first message in a brand-new
   *  thread. The text reaches the LLM (ACE's get_messages_for_agent does not
   *  filter on `hidden`) but is suppressed from the chat UI. Workaround for
   *  ACE's prompt template not exposing the wire metadata to the LLM in any
   *  reliable form — the model parses case_id from this seed line directly. */
  seedContext?: string
  selectedThreadId?: string | null
  existingThreadLoadScrollBehavior?: ScrollBehavior
  /**
   * When true, this conversation drives the browser tab (badged favicon +
   * title prefix) from its live state. Should be enabled on exactly one
   * surface — the dashboard chat — so controllers don't fight over the tab.
   */
  enableFaviconStatus?: boolean
}) {
  const [value, setValue] = useState(initialPrompt || '')
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([])
  const [hasUserSentMessage, setHasUserSentMessage] = useState(false)
  const [threadId, setThreadId] = useState<string | null>(initialSessionId || null)
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false)
  const [isAudioRenderingActive, setIsAudioRenderingActive] = useState(false)
  const [isAudioUiHeld, setIsAudioUiHeld] = useState(false)
  // Set by the right-arrow action on a client-list artifact row. Consumed by
  // the effect below once the chip is committed to chat-context state, so the
  // outgoing message metadata carries the correct client_code.
  const [pendingClientBriefPrompt, setPendingClientBriefPrompt] = useState<string | null>(null)
  const chatRootRef = useRef<HTMLElement>(null)
  const conversationScrollRef = useRef<HTMLDivElement>(null)
  const shouldStickToBottomRef = useRef(true)
  const pendingExistingThreadScrollRef = useRef<string | null>(selectedThreadId ?? null)
  const lastExistingThreadScrollCandidateRef = useRef<string | null>(selectedThreadId ?? null)
  const existingThreadScrollCleanupRef = useRef<(() => void) | null>(null)
  const lastUnavailableThreadRef = useRef<string | null>(null)
  const composerFieldRef = useRef<HTMLDivElement>(null)
  const transcriptionRequestRef = useRef(0)
  const audioUiReleaseTimerRef = useRef<number | null>(null)
  const autoSendInitialPromptRef = useRef(false)
  const composerIsEmpty = value.length === 0
  // Pause the rotation while the user is typing; resume when they clear the
  // field. Index advances at most once per interval tick.
  useEffect(() => {
    if (!composerIsEmpty) return
    const id = window.setInterval(() => {
      setPlaceholderIndex((current) => (current + 1) % COMPOSER_PLACEHOLDERS.length)
    }, COMPOSER_PLACEHOLDER_ROTATION_MS)
    return () => window.clearInterval(id)
  }, [composerIsEmpty])

  useEffect(() => {
    const root = composerFieldRef.current
    if (!root) return

    const syncAudioRenderingState = () => {
      const hasAudioRendering = composerHasAudioRendering(root)
      setIsAudioRenderingActive(hasAudioRendering)
      if (hasAudioRendering) {
        if (audioUiReleaseTimerRef.current != null) {
          window.clearTimeout(audioUiReleaseTimerRef.current)
          audioUiReleaseTimerRef.current = null
        }
        setIsAudioUiHeld(true)
        return
      }

      if (audioUiReleaseTimerRef.current != null) return
      audioUiReleaseTimerRef.current = window.setTimeout(() => {
        audioUiReleaseTimerRef.current = null
        setIsAudioUiHeld(false)
      }, AUDIO_UI_RELEASE_DELAY_MS)
    }

    syncAudioRenderingState()
    const observer = new MutationObserver(syncAudioRenderingState)
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    })
    return () => {
      observer.disconnect()
      if (audioUiReleaseTimerRef.current != null) {
        window.clearTimeout(audioUiReleaseTimerRef.current)
        audioUiReleaseTimerRef.current = null
      }
    }
  }, [])

  const {
    activeThreadId,
    cancel,
    isLoading: chatIsLoading,
    reset,
    sendMessage,
  } = useStreetBeatChat()
  const { submitFeedback } = useFeedback()
  const { speechToText } = useAudio()
  const { isLoading: flowIsLoading } = useConversationFlow()
  const { clearAllErrors, hasAnyError } = useChatErrors()
  const isThreadControlled = selectedThreadId !== undefined || Boolean(onThreadChange)
  const effectiveThreadId = isThreadControlled ? (selectedThreadId ?? null) : threadId
  const lastSelectedThreadId = useRef<string | null | undefined>(selectedThreadId)
  const surface = useChatRuntimeSurface({
    selectedThreadId: effectiveThreadId,
    onThreadChange: (nextThreadId, thread) => {
      if (isThreadControlled) {
        onThreadChange?.(nextThreadId ?? null, thread ?? null)
      } else {
        setThreadId(nextThreadId ?? null)
      }
      if (nextThreadId && nextThreadId !== initialSessionId) {
        onThreadCreated?.(nextThreadId)
      }
    },
  })
  const agentIsResponding = chatIsLoading || surface.composerDisabled

  useEffect(() => {
    if (!isThreadControlled || !effectiveThreadId || !onSelectedThreadUnavailable) return
    const isSelectedThreadHydrating =
      surface.phase === 'hydrating-thread' || surface.phase === 'switching-thread'
    const isEmptySelectedThreadFailure =
      hasAnyError && surface.activeThreadId === effectiveThreadId && !surface.hasConversation

    if (!hasAnyError) return
    if (!isSelectedThreadHydrating && !isEmptySelectedThreadFailure) return
    if (lastUnavailableThreadRef.current === effectiveThreadId) return

    lastUnavailableThreadRef.current = effectiveThreadId
    onSelectedThreadUnavailable(effectiveThreadId)
    clearAllErrors()
  }, [
    clearAllErrors,
    effectiveThreadId,
    hasAnyError,
    isThreadControlled,
    onSelectedThreadUnavailable,
    surface.activeThreadId,
    surface.hasConversation,
    surface.phase,
  ])

  // The browser-tab favicon must reflect an actual RUN on the open chat — not
  // the reconnect/hydration that `chatIsLoading` also covers, which otherwise
  // flashes thinking → done when you merely open an idle thread. Use
  // `composerDisabled` (a run is generating; steady for the whole run) gated on
  // the runtime being `active`, so the 'hydrating-thread' / 'switching-thread'
  // reconnect phases never light the tab. Idle threads stay the plain mark.
  const faviconResponding = surface.phase === 'active' && surface.composerDisabled

  useChatFavicon({
    enabled: enableFaviconStatus,
    threadId: effectiveThreadId,
    isResponding: faviconResponding,
    hasQuestion: Boolean(surface.pendingActionBarProps.action),
  })

  // Client chips are owned per-thread by the dashboard chat. The proposal-
  // editor chat already drives its own hidden seed line (case_id /
  // advisor_code) via `seedContext`, so on that surface we keep the pickers
  // hidden to avoid two competing context schemas in a single thread.
  const contextEnabled = !seedContext
  const chatContext = useChatContextSelection(contextEnabled ? effectiveThreadId : null)

  useEffect(() => {
    if (!isThreadControlled || lastSelectedThreadId.current === selectedThreadId) return
    const previousId = lastSelectedThreadId.current
    lastSelectedThreadId.current = selectedThreadId
    if (selectedThreadId) {
      pendingExistingThreadScrollRef.current = selectedThreadId
    }
    // Pending → real id is the SDK assigning an id to the conversation the user
    // was composing into (first send). Preserve the in-flight composer value,
    // attachments, and client tag — useChatContextSelection migrates the
    // pending tag onto the real thread key on the same transition.
    if (!previousId && selectedThreadId) return
    setValue('')
    setAttachments([])
    setHasUserSentMessage(false)
    // Per-thread chat-context rehydration is owned by useChatContextSelection.
    // Calling clearAll here would stomp over the destination thread's stored
    // tag (the persistence effect writes EMPTY back to storage). For thread
    // switches and resets, the hook already restores the right value.
    if (selectedThreadId === null) {
      reset()
    }
  }, [isThreadControlled, reset, selectedThreadId])

  useEffect(() => {
    const root = chatRootRef.current
    if (!root) return

    const handleClientCodeSelected = (event: Event) => {
      const detail = (event as CustomEvent<ClientCodeSelectedDetail>).detail
      if (!contextEnabled || !detail?.client) return
      chatContext.setClient(detail.client)
      requestAnimationFrame(() => {
        composerFieldRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()
      })
    }

    const handleClientBriefRequested = (event: Event) => {
      const detail = (event as CustomEvent<ClientBriefRequestedDetail>).detail
      if (!contextEnabled || !detail?.client || !detail.prompt) return
      chatContext.setClient(detail.client)
      // Defer to the auto-send effect: setClient is async, and send() reads
      // chat-context metadata synchronously. Queueing the prompt as state lets
      // the effect fire on the commit where the chip is already in scope.
      setPendingClientBriefPrompt(detail.prompt)
    }

    root.addEventListener(CLIENT_CODE_SELECTED_EVENT, handleClientCodeSelected)
    root.addEventListener(CLIENT_BRIEF_REQUESTED_EVENT, handleClientBriefRequested)
    return () => {
      root.removeEventListener(CLIENT_CODE_SELECTED_EVENT, handleClientCodeSelected)
      root.removeEventListener(CLIENT_BRIEF_REQUESTED_EVENT, handleClientBriefRequested)
    }
  }, [chatContext, contextEnabled])

  // Cross-route chip hand-off. Mirrors `handleClientBriefRequested` /
  // `handleClientCodeSelected` for the case where the trigger lives outside
  // this provider's DOM (e.g. the Da fare list dispatches via route state
  // instead of a bubbling DOM event). Fires once on mount when a context is
  // forwarded from the host route; subsequent chip changes flow through the
  // normal in-thread paths.
  const initialClientContextAppliedRef = useRef(false)
  useEffect(() => {
    if (initialClientContextAppliedRef.current) return
    if (!contextEnabled || !initialClientContext) return
    initialClientContextAppliedRef.current = true
    chatContext.setClient(initialClientContext)
    // When the host also wants the prompt auto-sent, queue it via the same
    // `pendingClientBriefPrompt` channel the in-chat brief flow uses so the
    // outgoing message metadata picks up the chip after it commits. When
    // there is no prompt (e.g. "Esplora cliente"), just nudge focus to the
    // composer so the banker can keep typing.
    if (autoSendInitialPrompt && initialPrompt?.trim()) {
      setPendingClientBriefPrompt(initialPrompt.trim())
    } else {
      requestAnimationFrame(() => {
        composerFieldRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus()
      })
    }
  }, [autoSendInitialPrompt, chatContext, contextEnabled, initialClientContext, initialPrompt])

  const prompts = useMemo<WelcomePrompt[]>(() => {
    if (!showSuggestedPrompts) return []
    const values = suggestedPrompts?.length ? suggestedPrompts : SUGGESTED_PROMPTS
    return values.map((text, index) => ({ id: `prompt-${index}`, text }))
  }, [showSuggestedPrompts, suggestedPrompts])
  const conversationStarted = hasUserSentMessage || surface.conversationStarted
  const transcriptScrollKey = conversationScrollKey(surface.conversationViewProps.turns)

  const scrollConversationToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const scrollPort = conversationScrollRef.current
    if (!scrollPort) return
    if (typeof scrollPort.scrollTo === 'function') {
      scrollPort.scrollTo({ top: scrollPort.scrollHeight, behavior })
      return
    }
    scrollPort.scrollTop = scrollPort.scrollHeight
  }, [])

  const stopExistingThreadBottomLock = useCallback(() => {
    existingThreadScrollCleanupRef.current?.()
    existingThreadScrollCleanupRef.current = null
  }, [])

  const startExistingThreadBottomLock = useCallback(
    (behavior: ScrollBehavior) => {
      stopExistingThreadBottomLock()

      const scrollPort = conversationScrollRef.current
      if (!scrollPort) return

      let active = true
      const animationFrames: number[] = []
      const timeouts: number[] = []
      let resizeObserver: ResizeObserver | null = null

      const scroll = (nextBehavior: ScrollBehavior = 'auto') => {
        if (!active) return
        scrollConversationToBottom(nextBehavior)
      }

      const scheduleFrame = (callback: () => void) => {
        const id = window.requestAnimationFrame(callback)
        animationFrames.push(id)
      }

      scroll(behavior)
      scheduleFrame(() => {
        scroll()
        scheduleFrame(scroll)
      })

      for (const delay of EXISTING_THREAD_LOAD_SCROLL_FALLBACK_DELAYS_MS) {
        timeouts.push(window.setTimeout(scroll, delay))
      }

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => scroll())
        resizeObserver.observe(scrollPort)
        const content = scrollPort.firstElementChild
        if (content instanceof HTMLElement) {
          resizeObserver.observe(content)
        }
      }

      timeouts.push(
        window.setTimeout(() => {
          stopExistingThreadBottomLock()
        }, EXISTING_THREAD_LOAD_SCROLL_SETTLE_MS),
      )

      existingThreadScrollCleanupRef.current = () => {
        active = false
        resizeObserver?.disconnect()
        for (const id of animationFrames) window.cancelAnimationFrame(id)
        for (const id of timeouts) window.clearTimeout(id)
      }
    },
    [scrollConversationToBottom, stopExistingThreadBottomLock],
  )

  const handleConversationScroll = useCallback(() => {
    const scrollPort = conversationScrollRef.current
    if (!scrollPort) return
    shouldStickToBottomRef.current = isNearConversationBottom(scrollPort)
  }, [])

  useLayoutEffect(() => {
    if (surface.screen !== 'conversation') return
    const scrollPort = conversationScrollRef.current
    if (!scrollPort) return
    if (isThreadControlled && !effectiveThreadId) {
      lastExistingThreadScrollCandidateRef.current = null
    }
    if (
      isThreadControlled &&
      effectiveThreadId &&
      lastExistingThreadScrollCandidateRef.current !== effectiveThreadId
    ) {
      lastExistingThreadScrollCandidateRef.current = effectiveThreadId
      pendingExistingThreadScrollRef.current = effectiveThreadId
    }
    if (
      pendingExistingThreadScrollRef.current &&
      pendingExistingThreadScrollRef.current === effectiveThreadId &&
      surface.conversationViewProps.turns.length > 0
    ) {
      pendingExistingThreadScrollRef.current = null
      shouldStickToBottomRef.current = true
      startExistingThreadBottomLock(existingThreadLoadScrollBehavior)
      return
    }

    const shouldScrollToBottom = isNearConversationBottom(scrollPort)
    shouldStickToBottomRef.current = shouldScrollToBottom
    if (!shouldScrollToBottom) return

    scrollConversationToBottom()
  }, [
    isThreadControlled,
    scrollConversationToBottom,
    startExistingThreadBottomLock,
    effectiveThreadId,
    existingThreadLoadScrollBehavior,
    surface.conversationViewProps.isLoading,
    surface.conversationViewProps.isStreaming,
    surface.screen,
    transcriptScrollKey,
  ])

  useEffect(() => stopExistingThreadBottomLock, [stopExistingThreadBottomLock])

  const send = useCallback(
    (text: string) => {
      const message = text.trim()
      if ((!message && attachments.length === 0) || agentIsResponding) return
      setHasUserSentMessage(true)

      void (async () => {
        const payload: AttachmentContent[] | undefined =
          attachments.length > 0
            ? await serializeAttachmentFiles(attachments.map((item) => item.file))
            : undefined
        const isFirstTurn = activeThreadId == null
        // First send in a brand-new thread → SDK fires createThreadAndStart and
        // the metadata becomes Thread.metadata. Subsequent sends → continueThread
        // and metadata becomes Message.metadata on the user turn being sent.
        const parentMetadata = isFirstTurn ? threadMetadata : buildMessageMetadata?.()
        const contextMetadata = contextEnabled ? chatContext.buildMessageMetadata() : undefined
        const metadata: Record<string, unknown> | undefined =
          parentMetadata || contextMetadata
            ? { ...(parentMetadata ?? {}), ...(contextMetadata ?? {}) }
            : undefined
        // On the first turn of a brand-new thread, prepend a hidden user message
        // carrying the seed context. The LLM sees it; the UI doesn't. The
        // dashboard surface uses the chat-context seed; the proposal-editor
        // surface uses the parent-provided seed (one or the other, never both).
        const effectiveSeed = contextEnabled ? chatContext.buildSeedContextLine() : seedContext
        const visibleContextLine = contextEnabled
          ? chatContext.buildVisibleMessageLine()
          : undefined
        const visibleMessage = visibleContextLine
          ? [message, visibleContextLine].filter(Boolean).join('\n\n')
          : message
        const content: string | MessageInput[] =
          isFirstTurn && effectiveSeed
            ? [
                { content: effectiveSeed, hidden: true },
                { content: visibleMessage, hidden: false },
              ]
            : visibleMessage
        await sendMessage(content, payload, metadata)
        setValue('')
        setAttachments([])
      })()
    },
    [
      activeThreadId,
      agentIsResponding,
      attachments,
      buildMessageMetadata,
      chatContext,
      contextEnabled,
      seedContext,
      sendMessage,
      threadMetadata,
    ],
  )

  useEffect(() => {
    if (!autoSendInitialPrompt || autoSendInitialPromptRef.current) return
    // When the host forwards a client chip too, the chip-aware effect above
    // routes the prompt through `pendingClientBriefPrompt` so it waits for
    // `chatContext.setClient` to commit before sending. Skipping here keeps
    // both effects from racing to send the same prompt twice.
    if (initialClientContext && contextEnabled) return
    const prompt = initialPrompt?.trim()
    if (!prompt || agentIsResponding) return
    autoSendInitialPromptRef.current = true
    send(prompt)
  }, [
    agentIsResponding,
    autoSendInitialPrompt,
    contextEnabled,
    initialClientContext,
    initialPrompt,
    send,
  ])

  // Auto-send the brief prompt queued by the client-list artifact's right-arrow
  // action. Waits for (a) the chip to land in chat-context so the outgoing
  // metadata is scoped to the same client and (b) the agent to be idle.
  useEffect(() => {
    if (!pendingClientBriefPrompt) return
    if (!contextEnabled) return
    if (agentIsResponding) return
    if (!chatContext.selection.client) return
    const prompt = pendingClientBriefPrompt
    setPendingClientBriefPrompt(null)
    send(prompt)
  }, [
    agentIsResponding,
    chatContext.selection.client,
    contextEnabled,
    pendingClientBriefPrompt,
    send,
  ])

  // Register the imperative send function with the parent (e.g. proposal editor
  // route) so external callers (block-menu actions) can dispatch messages into
  // this conversation without needing to be inside the SDK provider tree.
  // Uses a layout-effect so the ref is populated before the first paint.
  useEffect(() => {
    onRegisterSend?.(send)
  }, [onRegisterSend, send])

  // Bridge the SDK conversation-flow state out of the provider so the
  // proposal editor route (which lives outside this provider) can derive
  // its freeze-during-agent-run UI state without invoking SDK hooks itself.
  useEffect(() => {
    onRegisterFlow?.({ isLoading: flowIsLoading })
  }, [onRegisterFlow, flowIsLoading])

  useEffect(() => {
    onConversationStartedChange?.(conversationStarted)
  }, [conversationStarted, onConversationStartedChange])

  const handleFileDrop = useCallback((files: File[]) => {
    setAttachments((current) =>
      [
        ...current,
        ...files.filter((file) => file.size <= MAX_ATTACHMENT_BYTES).map(makeAttachment),
      ].slice(0, 8),
    )
  }, [])

  const handleAudioRecorded = useCallback(
    async (result: { blob: Blob; format: string }) => {
      const requestId = transcriptionRequestRef.current + 1
      transcriptionRequestRef.current = requestId
      setIsTranscribingAudio(true)

      try {
        const base64 = await blobToBase64(result.blob)
        const transcript = await speechToText({
          base64,
          format: audioFormatFromMimeType(result.format || result.blob.type),
        })
        if (transcriptionRequestRef.current !== requestId) return
        const text = transcript.text.trim()
        if (!text) return
        setValue((current) => (current.trim() ? `${current.trim()} ${text}` : text))
      } finally {
        if (transcriptionRequestRef.current === requestId) {
          setIsTranscribingAudio(false)
        }
      }
    },
    [speechToText],
  )

  const handleProcessingCancel = useCallback(() => {
    transcriptionRequestRef.current += 1
    setIsTranscribingAudio(false)
  }, [])

  const handleStop = useCallback(() => {
    void cancel()
  }, [cancel])

  const handleFeedback = useCallback(
    (data: {
      messageId: string
      threadId?: string
      satisfaction: number
      explanation?: string
    }) => {
      const request: FeedbackRequest = {
        message_id: data.messageId,
        satisfaction: data.satisfaction,
        ...(data.explanation ? { explanation: data.explanation } : {}),
      }
      void submitFeedback(request, { threadId: data.threadId ?? activeThreadId })
    },
    [activeThreadId, submitFeedback],
  )

  const showConversationSection =
    surface.screen === 'conversation' ||
    surface.screen === 'loading' ||
    (surface.screen === 'welcome' && showWelcomeHeading)

  // Capture-phase Enter blocker. While the agent is responding the textarea
  // stays editable so the user can draft their next message, but the SDK's
  // internal `onKeyDown` clears the controlled value *before* invoking our
  // `onSend` — so without this intercept the user would see their draft
  // disappear when they hit Enter even though our `send` guard refuses to
  // submit. Swallowing the event in the capture phase keeps the draft intact.
  const handleComposerKeyDownCapture = (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing &&
      agentIsResponding
    ) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  const showComposerPlaceholder = shouldShowComposerPlaceholder({
    composerIsEmpty,
    isAudioRenderingActive,
    isTranscribingAudio,
  })
  const keepAudioComposerLayout = isAudioRenderingActive || isTranscribingAudio || isAudioUiHeld

  const composerField = (
    <div
      className={`agent-composer__field${
        keepAudioComposerLayout ? ' agent-composer__field--audio-active' : ''
      }`}
      onKeyDownCapture={handleComposerKeyDownCapture}
      ref={composerFieldRef}
    >
      {showComposerPlaceholder ? (
        <div className="agent-composer__rotating-placeholder" aria-hidden="true">
          {conversationStarted ? (
            <span>Scrivi un messaggio...</span>
          ) : (
            <span key={placeholderIndex}>{COMPOSER_PLACEHOLDERS[placeholderIndex]}</span>
          )}
        </div>
      ) : null}
      <ChatComposer
        value={value}
        onChange={setValue}
        onSend={() => send(value)}
        /* Keep the composer "live" while the agent is running so the user can
         * draft their next message during a turn. The send button is still
         * blocked: visually via the `agent-composer--loading` class (see
         * styles.css) and at runtime via the `agentIsResponding` guard inside
         * `send`. The SDK stop control is exposed whenever the current turn is
         * loading and the composer is otherwise unable to send. */
        disabled={false}
        placeholder=" "
        maxRows={6}
        onAudioRecorded={handleAudioRecorded}
        isProcessing={isTranscribingAudio}
        onProcessingCancel={handleProcessingCancel}
        onStop={chatIsLoading ? handleStop : undefined}
        /* When chat-context tagging is on the chip bar owns file rendering so
         * files and the client tag share one row. On the proposal-editor
         * surface there is no chip bar, so we let the SDK render its native
         * AttachmentPreview row above the input. */
        attachments={contextEnabled ? undefined : attachments}
        onAttachmentRemove={
          contextEnabled
            ? undefined
            : (attachmentId) => {
                setAttachments((current) => current.filter((item) => item.id !== attachmentId))
              }
        }
        onFileDrop={handleFileDrop}
        fileInputAccept={ATTACHMENT_ACCEPT}
        maxFileSizeBytes={MAX_ATTACHMENT_BYTES}
        maxTotalSizeBytes={MAX_ATTACHMENT_BYTES * 2}
        executionModeElement={
          contextEnabled ? (
            <ClientPickerMenu
              selectedClientCode={chatContext.selection.client?.client_code ?? null}
              onSelect={chatContext.setClient}
            />
          ) : undefined
        }
        suggestedPrompts={prompts}
        suggestedPromptSize="sm"
        suggestedPromptTruncate
        onSuggestedPromptClick={(promptText) => send(promptText)}
        conversationStarted={conversationStarted}
      />
    </div>
  )

  // When chat-context tagging is on (dashboard surface) the chip bar and the
  // SDK composer share a single rounded panel via ComposerStack. The
  // proposal-editor surface keeps the SDK composer's native panel — no stack.
  const composerPanel = contextEnabled ? (
    <ComposerStack>
      <ContextChipBar
        attachments={attachments}
        onRemoveAttachment={(attachmentId) =>
          setAttachments((current) => current.filter((item) => item.id !== attachmentId))
        }
        selection={chatContext.selection}
        onRemoveClient={() => chatContext.setClient(null)}
      />
      {composerField}
    </ComposerStack>
  ) : (
    composerField
  )

  return (
    <section ref={chatRootRef} className="agent-chat agent-chat--ace" aria-label="Agent chat">
      {showNewChatButton ? (
        <div className="agent-chat__floating-actions">
          <IconButton
            size="md"
            onClick={() => {
              reset()
              if (isThreadControlled) {
                onThreadChange?.(null, null)
              } else {
                setThreadId(null)
              }
              setValue('')
              setAttachments([])
              setHasUserSentMessage(false)
              // Per-thread chat-context state is owned by useChatContextSelection
              // and rehydrates from PENDING_KEY on the real→null transition.
              // We deliberately don't clear it here: calling clearAll while
              // threadId still points at the outgoing thread would write EMPTY
              // back to that thread's storage key and lose its client tag.
            }}
            disabled={agentIsResponding}
            title="Nuova chat"
            aria-label="Nuova chat"
          >
            <Plus aria-hidden />
          </IconButton>
        </div>
      ) : null}

      {showConversationSection ? (
        <div
          className="agent-chat__conversation agent-chat__conversation--ace"
          onScroll={handleConversationScroll}
          ref={conversationScrollRef}
        >
          {surface.screen === 'welcome' && showWelcomeHeading ? (
            <main className="agent-chat__main agent-chat__main--welcome">
              <div className="agent-empty">
                <h2>What should we check next?</h2>
              </div>
            </main>
          ) : null}
          {surface.screen === 'loading' ? (
            <div className="agent-empty" role="status">
              <Loader variant="blocks" showLabel={false} size="xl" />
            </div>
          ) : null}
          {surface.screen === 'conversation' ? (
            <CredemConversationView
              {...surface.conversationViewProps}
              agentName="Credem AI"
              threadId={activeThreadId ?? undefined}
              maxWidth="calc(100% - (var(--chat-composer-edge-inset, 20px) * 2))"
              breathingRoomEnabled
              breathingRoomThinkingLabel="Sto pensando..."
              messageActions={{
                copy: { show: true, onCopy: copyMessageTextToClipboard },
                feedback: {
                  show: true,
                  variant: FeedbackVariant.LIKE_DISLIKE,
                  onSubmit: handleFeedback,
                  requireExplanationOnDislike: true,
                },
                timestamp: {
                  show: true,
                  format: formatChatTimestamp,
                },
              }}
              rendering={{ interruptedText: 'Risposta interrotta.' }}
              artifacts={ARTIFACT_RENDERING}
            />
          ) : null}
        </div>
      ) : null}

      {surface.pendingActionBarProps.action ? (
        <PendingActionBar action={surface.pendingActionBarProps.action} />
      ) : null}

      <div
        className={`agent-composer agent-composer--ace${
          agentIsResponding ? ' agent-composer--loading' : ''
        }`}
      >
        {composerPanel}
      </div>
    </section>
  )
}

export function AgentApp({
  config: agentConfig,
  initialClientContext,
  onRegisterSend,
  onRegisterFlow,
  onConversationStartedChange,
  onThreadCreated,
  onThreadChange,
  onSelectedThreadUnavailable,
  threadMetadata,
  buildMessageMetadata,
  seedContext,
  selectedThreadId,
  enableFaviconStatus,
  existingThreadLoadScrollBehavior,
}: {
  config?: Partial<AgentConfig>
  /** Forwarded to `AceConversation`; see prop docs there. */
  initialClientContext?: ClientRef
  onRegisterSend?: (fn: (text: string) => void) => void
  onRegisterFlow?: (state: { isLoading: boolean }) => void
  onConversationStartedChange?: (started: boolean) => void
  onThreadCreated?: (threadId: string) => void
  onThreadChange?: (threadId: string | null, thread?: Thread | null) => void
  onSelectedThreadUnavailable?: (threadId: string) => void
  threadMetadata?: Record<string, unknown>
  buildMessageMetadata?: () => Record<string, unknown> | undefined
  seedContext?: string
  selectedThreadId?: string | null
  /** Forwarded to `AceConversation`; drives the browser tab. Enable on the
   *  dashboard chat only. */
  enableFaviconStatus?: boolean
  /** Scroll behavior used for the one-shot jump after loading an existing thread. */
  existingThreadLoadScrollBehavior?: ScrollBehavior
}) {
  const state = useAceSession()

  if (state.status === 'loading') {
    return (
      <section className="agent-chat agent-chat--ace" aria-label="Agent chat">
        <div className="agent-empty" role="status">
          <Loader variant="blocks" showLabel={false} size="xl" />
        </div>
      </section>
    )
  }

  const session = state.status === 'ready' ? state.session : null
  const initialThreadId = session ? agentConfig?.initialSessionId : undefined

  const config = buildAgentChatConfig(session, agentConfig)

  return (
    <ChatRuntimeProvider
      config={config}
      locale="it"
      initialThreadId={selectedThreadId === undefined ? initialThreadId : undefined}
      threadId={selectedThreadId}
    >
      <TranslationsProvider
        locale="it"
        overrides={{ thinking: 'Sto pensando...', thought: 'Processo' }}
      >
        <AceConversation
          initialPrompt={agentConfig?.initialPrompt}
          autoSendInitialPrompt={agentConfig?.autoSendInitialPrompt}
          initialClientContext={initialClientContext}
          suggestedPrompts={agentConfig?.suggestedPrompts}
          showSuggestedPrompts={agentConfig?.showSuggestedPrompts}
          showWelcomeHeading={agentConfig?.showWelcomeHeading}
          showNewChatButton={agentConfig?.showNewChatButton}
          onConversationStartedChange={onConversationStartedChange}
          onRegisterSend={onRegisterSend}
          onRegisterFlow={onRegisterFlow}
          initialSessionId={initialThreadId}
          onThreadCreated={onThreadCreated}
          onThreadChange={onThreadChange}
          onSelectedThreadUnavailable={onSelectedThreadUnavailable}
          threadMetadata={threadMetadata}
          buildMessageMetadata={buildMessageMetadata}
          seedContext={seedContext}
          selectedThreadId={selectedThreadId}
          enableFaviconStatus={enableFaviconStatus}
          existingThreadLoadScrollBehavior={existingThreadLoadScrollBehavior}
        />
      </TranslationsProvider>
    </ChatRuntimeProvider>
  )
}
